import AttachFileIcon from "@mui/icons-material/AttachFile";
import {
	Box,
	Badge,
	Checkbox,
	Chip,
	FormControl,
	Grid,
	IconButton,
	InputLabel,
	MenuItem,
	Paper,
	Select,
	SelectChangeEvent,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableFooter,
	TableHead,
	TablePagination,
	TableRow,
	Tooltip,
	Typography,
} from "@mui/material";
import dayjs from "dayjs";
import moment from "moment";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { DateViewMonthYear } from "../../../shared/components/DateViewMonthYear/DataViewMonthYear";
import { LoadingComp } from "../../../shared/components/loading/Loading";
import { Environment } from "../../../shared/environment";
import { ISinisterValues as OriginalISinisterValues, SinisterService } from "../../../shared/services/api/sinister/SinisterService";
import { globalBackground, globalColorText, globalTranslate } from "../../../shared/utils";
import { ISinisterValuesUI } from "../types/ISinisterValuesUI";
import { ModalAddValues } from "./Modals/ModalAddValues";
import { ModalApproved } from "./Modals/ModalApproved";
import { ModalCanceled } from "./Modals/ModalCanceled";
import { ModalContested } from "./Modals/ModalContested";
import { ModalDetailValue } from "./Modals/ModalDetailValue";
import { ModalDocumentValue } from "./Modals/ModalDocumentValue";
import { ModalRefused } from "./Modals/ModalRefused";
import { RowMenu } from "./RowMenu";

const VALUE_STATUS_OPTIONS: Array<{ value: string; label: string }> = [
	{ value: "", label: "Todos" },
	{ value: "open", label: "Aberto" },
	{ value: "approved", label: "Aprovado" },
	{ value: "contested", label: "Contestado" },
	{ value: "refused", label: "Recusado" },
	{ value: "canceled", label: "Cancelado" },
];

interface ISinisterValues extends OriginalISinisterValues {
	checked?: boolean;
}

interface Props {
	SinisterId: string;
	refreshTrigger?: boolean;
	onSelectionChange?: (rows: ISinisterValues[]) => void;
	onRowCountChange?: (count: number) => void;
	onRowsLoaded?: (rows: ISinisterValues[]) => void;
}

const normalizeStatusForSoftChip = (status: string) => {
	if (status === "approved" || status === "accepted") return "issued";
	if (status === "provision") return "provisioned";
	if (status === "analysis") return "pending";
	return status;
};

export function TableSinisterValues({ SinisterId, refreshTrigger, onSelectionChange, onRowCountChange, onRowsLoaded }: Props) {
	const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
	const [idValue, setIdValue] = useState("");
	const [rowSelect, setRowSelect] = useState<ISinisterValues>();
	const [isLoading, setIsLoading] = useState(false);
	const [rowsValue, setRowsValue] = useState<ISinisterValues[]>([]);
	const [totalCountValue, setTotalCountValue] = useState(0);
	const [searchParams, setSearchParams] = useSearchParams();
	const [isRefresh, setIsRefresh] = useState(false);
	const busca = useMemo(() => searchParams.get("busca") || "", [searchParams]);
	const pagina = useMemo(() => Number(searchParams.get("pagina") || "1"), [searchParams]);
	const tipo = useMemo(() => searchParams.get("tipo") || "", [searchParams]);
	// Padrão "open" (débitos em aberto) — pedido da operação (item 8, GARV2-1463/1464).
	const status = useMemo(() => searchParams.get("status") ?? "open", [searchParams]);
	const reference = useMemo(() => searchParams.get("reference") || "", [searchParams]);
	const rowsPerPage = useMemo(() => Number(searchParams.get("rowsPerPage") || String(Environment.LIMITE_LINHAS_VALORES_BORDEROS)), [searchParams]);

	const updateParams = useCallback(
		(next: { busca?: string; pagina?: string; tipo?: string; status?: string; reference?: string; rowsPerPage?: string }) => {
			setSearchParams(
				{
					busca,
					pagina: String(pagina),
					tipo,
					status,
					reference,
					rowsPerPage: String(rowsPerPage),
					...next,
				},
				{ replace: true }
			);
		},
		[busca, pagina, reference, rowsPerPage, setSearchParams, status, tipo]
	);
	const isValuesSelectable = (row: ISinisterValues) => row.status === "approved";
	const selectableRows = rowsValue.filter(isValuesSelectable);
	const allSelected = selectableRows.every((row) => row.checked);
	const someSelected = selectableRows.some((row) => row.checked);

	const [modalStates, setModalStates] = useState({
		openModalValuesSinister: false,
		openModalAproved: false,
		openModalContested: false,
		openModalCancel: false,
		openUpdateStatus: false,
		openModalRefused: false,
		openModalDocumentValue: false,
	});

	// Duplicar/espelhar lançamento (item 7) — modal próprio, separado do "Adicionar" da toolbar.
	const [duplicateFromRow, setDuplicateFromRow] = useState<ISinisterValues | null>(null);

	const handleRefresh = () => {
		setIsRefresh((prev) => !prev);
	};

	const handleDuplicate = (row: ISinisterValues) => {
		setDuplicateFromRow(row);
	};

	const handleToggleModal = useCallback((modal: keyof typeof modalStates, isOpen: boolean) => {
		setModalStates((prev) => ({ ...prev, [modal]: isOpen }));
	}, []);

	const handleSelectAllCheckboxChange = (checked: boolean) => {
		const newSet = new Set<string>();
		const updatedRows = rowsValue.map((row) => {
			if (isValuesSelectable(row)) {
				if (checked && row.id) newSet.add(row.id);
				return { ...row, checked: checked && !!row.id };
			}
			return row;
		});
		setRowsValue(updatedRows);
		setSelectedIds(newSet);
		onSelectionChange?.(updatedRows.filter((r) => r.checked));
	};

	const getAllValuesBySinister = () => {
		setIsLoading(true);

		SinisterService.getAllValues(pagina, busca, tipo, status, SinisterId, rowsPerPage, reference).then((result) => {
			setIsLoading(false);
			if (result instanceof Error) {
				alert(result.message);
			} else {
				setTotalCountValue(result.totalCount);
				setSelectedIds(new Set());
				const rows = result.data?.map((row) => ({
					...row,
					checked: false,
				}));
				setRowsValue(rows);
				onRowsLoaded?.(rows);
				onSelectionChange?.([]);
				onRowCountChange?.(rows.length);
			}
		});
	};

	useEffect(() => {
		getAllValuesBySinister();
	}, [pagina, busca, tipo, status, reference, SinisterId, rowsPerPage, isRefresh, refreshTrigger]);

	const handleCheckboxRowChange = (row: any) => {
		const newSet = new Set(selectedIds);
		row.checked = !row.checked;
		row.checked ? newSet.add(row.id) : newSet.delete(row.id);
		setSelectedIds(newSet);
		const updatedRows = rowsValue.map((r) => ({
			...r,
			checked: typeof r.id === "string" && newSet.has(r.id),
		}));
		setRowsValue(updatedRows);
		onSelectionChange?.(updatedRows.filter((r) => r.checked));
	};

	const handleChangePage = (_: unknown, newPage: number) => {
		updateParams({ pagina: String(newPage + 1) });
	};

	const handleChangeRowsPerPage = (newRowsPerPage: number) => {
		updateParams({ pagina: "1", rowsPerPage: String(newRowsPerPage) });
	};

	const goTo = (option: string, id: string, e: any, row: ISinisterValuesUI) => {
		e.preventDefault();
		setIdValue(id);
		setRowSelect(row);

		const modalMap = {
			aprovar: "openModalAproved",
			rejeitar: "openModalRefused",
			cancelar: "openModalCancel",
			contestar: "openModalContested",
		} as const;

		const modal = modalMap[option as keyof typeof modalMap];
		if (!modal) return;

		handleToggleModal(modal, true);
	};

	return (
		<>
			<LoadingComp isopen={isLoading} />
			<Grid container item spacing={2}>
				<Grid item xs={12}>
					<Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mb: 1, px: 1 }}>
						<FormControl size="small" sx={{ width: 150 }}>
							<InputLabel id="sinister-values-status-label" shrink>
								Status
							</InputLabel>
							<Select
								labelId="sinister-values-status-label"
								label="Status"
								value={status}
								displayEmpty
								notched
								onChange={(e: SelectChangeEvent) => updateParams({ pagina: "1", status: e.target.value })}
							>
								{VALUE_STATUS_OPTIONS.map((option) => (
									<MenuItem key={option.value || "todos"} value={option.value}>
										{option.label}
									</MenuItem>
								))}
							</Select>
						</FormControl>
						<Box sx={{ width: 150 }}>
							<DateViewMonthYear
								title="Competência"
								value={reference ? dayjs(`${reference}-01`) : null}
								onChange={(newValue) => {
									const nextReference = newValue
										? `${newValue.getFullYear()}-${String(newValue.getMonth() + 1).padStart(2, "0")}`
										: "";
									updateParams({ pagina: "1", reference: nextReference });
								}}
								disableFuture={false}
							/>
						</Box>
					</Stack>

					<TableContainer
						component={Paper}
						elevation={0}
						variant="elevation"
						sx={{ borderRadius: 0, overflow: "hidden", boxShadow: "none" }}
					>
						<Table size="small" stickyHeader aria-label="Tabela de valores">
							<TableHead>
								<TableRow sx={{ backgroundColor: "grey.100" }}>
									<TableCell padding="checkbox">
									<Checkbox
										checked={allSelected}
										indeterminate={!allSelected && someSelected}
										onChange={(e) => handleSelectAllCheckboxChange(e.target.checked)}
									/>
								</TableCell>
									<TableCell>
										<Typography variant="button">Débito</Typography>
									</TableCell>
									<TableCell align="right">
										<Typography variant="button">Valor</Typography>
									</TableCell>
									<TableCell align="right">
										<Typography variant="button">Aprovado</Typography>
									</TableCell>
									<TableCell align="center">
										<Typography variant="button">Vencimento</Typography>
									</TableCell>
									<TableCell align="center">
										<Typography variant="button">Lançamento</Typography>
									</TableCell>
									<TableCell>
										<Typography variant="button">Status</Typography>
									</TableCell>
									<TableCell align="center">
										<Typography variant="button">Anexos</Typography>
									</TableCell>
									<TableCell align="right">
										<Typography variant="button">Ações</Typography>
									</TableCell>
							</TableRow>
						</TableHead>

						<TableBody>
							{rowsValue.map((row) => (
									<TableRow
										key={row.id}
										hover
										sx={{
											"& .MuiTableCell-root": {
												py: 0.75,
											},
									}}
									>
										<TableCell padding="checkbox">
										<Checkbox
											checked={row.checked}
											disabled={!isValuesSelectable(row)}
											onChange={() => row.id && handleCheckboxRowChange(row)}
										/>
									</TableCell>
									<TableCell>
											<Stack spacing={0.25}>
												<Typography variant="body2" fontWeight={600} sx={{ lineHeight: 1.2 }}>
													{globalTranslate(row.typeId)}
												</Typography>
												<Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.2 }}>
													{row.reference}
												</Typography>
											</Stack>
									</TableCell>
										<TableCell align="right">
											<Typography variant="body2" fontWeight={500} color="text.secondary" sx={{ lineHeight: 1.2 }}>
												{Number(row.value).toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}
											</Typography>
										</TableCell>
										<TableCell align="right">
											<Typography variant="body2" fontWeight={500} color="text.secondary" sx={{ lineHeight: 1.2 }}>
												{Number(row.valueApproved).toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}
											</Typography>
										</TableCell>
										<TableCell align="center">
											<Typography variant="body2" sx={{ lineHeight: 1.2 }}>
												{moment(row.dueDate).format("L")}
											</Typography>
										</TableCell>
										<TableCell align="center">
											<Typography variant="body2" sx={{ lineHeight: 1.2 }}>
												{moment(row.createdAt).format("L")}
											</Typography>
										</TableCell>
									<TableCell>
											<Chip
												size="medium"
												label={globalTranslate(row.status)}
												clickable
												onClick={() => {
													setIdValue(`${row.id}`);
													setRowSelect(row);
													handleToggleModal("openModalValuesSinister", true);
												}}
												sx={{
													color: globalColorText(normalizeStatusForSoftChip(row.status)),
													backgroundColor: globalBackground(normalizeStatusForSoftChip(row.status)),
													fontWeight: 500,
													border: "0.5px solid #f1f1f1",
												}}
											/>
									</TableCell>
										<TableCell align="center">
											<Tooltip title="Anexos" placement="top">
												<IconButton
													size="small"
													onClick={() => {
														setIdValue(`${row.id}`);
														setRowSelect(row);
														handleToggleModal("openModalDocumentValue", true);
												}}
											>
												<Badge
													color="secondary"
													badgeContent={row?.sinisterValuesDocuments?.length}
													anchorOrigin={{ vertical: "top", horizontal: "left" }}
												>
													<AttachFileIcon fontSize="small" />
												</Badge>
											</IconButton>
											</Tooltip>
										</TableCell>
										<TableCell align="right">
											<RowMenu row={row} goTo={goTo} onDuplicate={handleDuplicate} />
										</TableCell>
								</TableRow>
							))}
						</TableBody>

						<TableFooter>
							{selectedIds.size > 0 && (
								<TableRow>
									<TableCell colSpan={5}>
										<Typography color="red" fontWeight="bold">
											Selecionado(s): {selectedIds.size}
										</Typography>
									</TableCell>
									<TableCell colSpan={4}>
										<Typography color="red" fontWeight="bold">
											Total:{" "}
											{rowsValue
												.filter((e) => e.checked)
												.reduce((acc, curr) => acc + Number(curr.valueApproved), 0)
												.toLocaleString("pt-br", { style: "currency", currency: "BRL" })}
										</Typography>
									</TableCell>
								</TableRow>
							)}
						</TableFooter>
						</Table>

						{/* Paginação (padrão financeiro) */}
						{totalCountValue > 0 && (
							<Box display="flex" justifyContent="flex-end" alignItems="center" mt={2} pr={2}>
								<TablePagination
									component="div"
									count={totalCountValue}
									page={Math.max(0, pagina - 1)}
									rowsPerPage={rowsPerPage}
									onPageChange={handleChangePage}
									onRowsPerPageChange={(e) => handleChangeRowsPerPage(parseInt(e.target.value, 10))}
									rowsPerPageOptions={[10, 25, 50]}
									labelRowsPerPage="Linhas por página:"
									labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count} registros`}
								/>
							</Box>
						)}
					</TableContainer>
				</Grid>
			</Grid>

			{/* Modals */}
			<ModalAddValues
				open={Boolean(duplicateFromRow)}
				onClose={() => setDuplicateFromRow(null)}
				sinisterId={SinisterId}
				duplicateFrom={duplicateFromRow ?? undefined}
				onSuccess={() => {
					setDuplicateFromRow(null);
					handleRefresh();
				}}
			/>
			<ModalDetailValue
				rowSelect={rowSelect}
				open={modalStates.openModalValuesSinister}
				onClose={() => handleToggleModal("openModalValuesSinister", false)}
				name={idValue}
			/>
			<ModalApproved
				rowSelect={rowSelect}
				open={modalStates.openModalAproved}
				onClose={() => handleToggleModal("openModalAproved", false)}
				onSuccess={() => handleRefresh()}
			/>
			<ModalCanceled
				rowSelect={rowSelect}
				open={modalStates.openModalCancel}
				onClose={() => handleToggleModal("openModalCancel", false)}
				onSuccess={() => handleRefresh()}
			/>
			<ModalRefused
				rowSelect={rowSelect}
				open={modalStates.openModalRefused}
				onClose={() => handleToggleModal("openModalRefused", false)}
				onSuccess={() => handleRefresh()}
			/>
			<ModalContested
				rowSelect={rowSelect}
				open={modalStates.openModalContested}
				onClose={() => handleToggleModal("openModalContested", false)}
				onSuccess={() => handleRefresh()}
			/>
			<ModalDocumentValue
				onClose={() => handleToggleModal("openModalDocumentValue", false)}
				open={modalStates.openModalDocumentValue}
				rowSelect={rowSelect}
				onSuccess={() => handleRefresh()}
			/>
		</>
	);
}
